import { Box, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { post } from '../../api';
import CardHeader from './CardHeader';
import { calculateCheckoutAmount, chargeNMITokenOnBackend, getCardExpiryFromLookup, getCardLast4FromLookup } from './nmiGateway';
import { OnlineCartProvider, useOnlineCart } from './OnlineCartContext';
import OnlineCartStepTransition from './OnlineCartStepTransition';
import OrderConfirmation from './OrderConfirmation';
import QuestionRenderer from './QuestionRenderer';
import SSNCollection from './SSNCollection';
import StepWrapper from './StepWrapper';
import { ONLINE_CART_TOKENS } from './style';
import NoDataPresentUI from '../../components/NoDataPresentUI';
import { DEFAULT_ONLINE_CART_RECORD_ID, getPersistedOnlineCartAnswerId } from './constants';
import { areAddressesDifferent, isValidDob, scrollToFieldAndFocus, toTrimmedString } from './validation';
import { getStateByCode } from './dummyData';

const ENABLE_NEXT_BUTTON_LOADER = true;

function OnlineCartScreen() {
    const {
        state,
        currentScreen,
        hasFormData,
        currentStep,
        totalSteps,
        sectionProgress,
        onlineCartRecord,
        onlineCartRecordLoading,
        onlineCartRecordError,
        recordId,
        setField,
        setFieldErrors,
        prevScreen,
        goToStep,
        validateCurrentScreen,
        saveAnswers,
    } = useOnlineCart();
    const cartProducts = onlineCartRecord?.products || [];
    const [transitionDirection, setTransitionDirection] = useState('forward');
    const [postCheckoutStage, setPostCheckoutStage] = useState('none');
    const [saveAnswerLoading, setSaveAnswerLoading] = useState(false);
    const hasInitializedPostCheckoutStageRef = useRef(false);
    const mainRef = useRef(null);
    const ssnSubmitHandlerRef = useRef(null);
    const shouldResumeConfirmation = Boolean(state.answers?.orderCompleted || state.answers?.paymentProcessed);
    const shouldShowConfirmation = postCheckoutStage === 'confirmation' || (!hasInitializedPostCheckoutStageRef.current && shouldResumeConfirmation);

    const resetStepScroll = useCallback(() => {
        const main = mainRef.current;
        if (!main) {
            return;
        }
        if (typeof main.scrollTo === 'function') {
            main.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        } else {
            main.scrollTop = 0;
        }
        main.scrollTop = 0;
    }, []);

    useLayoutEffect(() => {
        if (onlineCartRecordLoading) {
            return;
        }
        resetStepScroll();
    }, [currentScreen?.id, onlineCartRecordLoading, resetStepScroll, shouldShowConfirmation]);

    useEffect(() => {
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return undefined;
        }

        const root = document.getElementById('root');
        const previousBodyOverflow = document.body.style.overflow;
        const previousRootOverflow = root?.style?.overflow;
        const mediaQuery = window.matchMedia('(max-width: 600px)');

        const applyMobileOverflowLock = () => {
            document.body.style.overflow = 'hidden';
            if (root) {
                root.style.overflow = 'hidden';
            }
        };

        const restoreOverflow = () => {
            document.body.style.overflow = previousBodyOverflow;
            if (root) {
                root.style.overflow = previousRootOverflow || '';
            }
        };

        const handleViewportChange = (event) => {
            if (event.matches) {
                applyMobileOverflowLock();
            } else {
                restoreOverflow();
            }
        };

        if (mediaQuery.matches) {
            applyMobileOverflowLock();
        } else {
            restoreOverflow();
        }

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', handleViewportChange);
        } else if (typeof mediaQuery.addListener === 'function') {
            mediaQuery.addListener(handleViewportChange);
        }

        return () => {
            if (typeof mediaQuery.removeEventListener === 'function') {
                mediaQuery.removeEventListener('change', handleViewportChange);
            } else if (typeof mediaQuery.removeListener === 'function') {
                mediaQuery.removeListener(handleViewportChange);
            }
            restoreOverflow();
        };
    }, []);

    useEffect(() => {
        if (onlineCartRecordLoading || hasInitializedPostCheckoutStageRef.current) {
            return;
        }

        if (shouldResumeConfirmation) {
            setPostCheckoutStage('confirmation');
        }

        hasInitializedPostCheckoutStageRef.current = true;
    }, [onlineCartRecordLoading, shouldResumeConfirmation]);

    const handleConsultationBooked = useCallback(
        (scheduledFor) => {
            setField('consultationBooked', true);
            setField('consultationScheduledFor', scheduledFor || '');
        },
        [setField]
    );
    const handleSkipConsultation = useCallback(() => {
        setField('consultationSkipped', true);
    }, [setField]);

    const scrollToFieldError = useCallback((fieldId, options = {}) => {
        scrollToFieldAndFocus(fieldId, {
            selectorTemplate: '[data-online-cart-field="{fieldId}"]',
            ...options
        });
    }, []);

    const dateOfBirthReset = (isNext)=>{
        if(isNext === false && (currentScreen?.id === 'state_of_formation' || currentScreen?.id === 'relay_bank')){
            if(currentScreen?.id === 'state_of_formation'){
                const field = currentScreen?.fields?.find(c=>c.id === 'dateOfBirth')
                const selectedStateCode = state?.answers?.stateOfFormation?.stateCode || '';
                const requiredStateCodes = Array.isArray(field.requiredWhenStateCodes) ? field.requiredWhenStateCodes : [];
                const isRequiredForState = requiredStateCodes.includes(selectedStateCode);

                if(isRequiredForState && !isValidDob(state?.answers?.dateOfBirth)){
                    let dob = state?.answers?.relayBankDateOfBirth;
                    dob = isValidDob(dob) ? dob : undefined
                    setField('dateOfBirth', dob)
                    setFieldErrors({
                        dateOfBirth: '',
                    })
                }else if(!isRequiredForState){
                    setField('dateOfBirth', undefined)
                    setFieldErrors({
                        dateOfBirth: '',
                    })
                }
            }

            if(currentScreen?.id === 'relay_bank'){
                const field = currentScreen?.fields?.find(c=>c.id === "relayBank")
                const isRelayBankSelected =  state?.answers?.relayBank === field?.optInDetails?.showForValue
                if(isRelayBankSelected && !isValidDob(state?.answers?.relayBankDateOfBirth)){
                    let dob = state?.answers?.dateOfBirth;
                    dob = isValidDob(dob) ? dob : undefined
                    setField('relayBankDateOfBirth', dob)
                    setFieldErrors({
                        relayBank: '',
                    })
                }
            }
        }
    }

    const checkMailingAddressDiffrent = (answers={}) => {
        const userInputAddress = {
            addressLine1: toTrimmedString(answers?.mailingAddress1),
            addressLine2: toTrimmedString(answers?.mailingAddress2),
            city: toTrimmedString(answers?.mailingCity),
            // state: toTrimmedString(answers?.mailingState),
            stateCode: toTrimmedString(answers?.mailingStateCode),
            zipCode: toTrimmedString(answers?.mailingZip),
        };
        const smartySuggestedAddress = {
            addressLine1: toTrimmedString(answers?.smartySuggestedMalingAddress?.addressLine1),
            addressLine2: toTrimmedString(answers?.smartySuggestedMalingAddress?.addressLine2),
            city: toTrimmedString(answers?.smartySuggestedMalingAddress?.city),
            // state: toTrimmedString(answers?.mailingState),
            stateCode: toTrimmedString(answers?.smartySuggestedMalingAddress?.stateCode),
            zipCode: toTrimmedString(answers?.smartySuggestedMalingAddress?.zipCode),
        };
        return areAddressesDifferent(userInputAddress, smartySuggestedAddress)
    };

    const handleNext = async () => {
        setTransitionDirection('forward');
        const { isValid, firstErrorFieldId } = validateCurrentScreen();
                
        if (!isValid) {
            scrollToFieldError(firstErrorFieldId);
            return false;
        }
        // debugger

        const isMailingAddressOverridden = Boolean(state?.answers?.mailingAddressOverride?.overridden);
        const isDiffrent = !isMailingAddressOverridden && currentScreen?.id === 'mailing_address' && checkMailingAddressDiffrent(state?.answers || {});
        if (isDiffrent) {
            const setMailingInvalid = (fieldErrors = {}) => {
                setFieldErrors(fieldErrors);
                setSaveAnswerLoader(false);
                scrollToFieldError('mailing_address', {
                    deferredScroll: true,
                    selectorTemplate: '[data-online-cart-error-screen="{fieldId}"]',
                });
            };
            try {
                setSaveAnswerLoader(true);
                const payload = {
                    addressLine1: toTrimmedString(state?.answers?.mailingAddress1),
                    addressLine2: toTrimmedString(state?.answers?.mailingAddress2),
                    city: toTrimmedString(state?.answers?.mailingCity),
                    // state: toTrimmedString(state?.answers?.mailingState),
                    stateCode: toTrimmedString(state?.answers?.mailingStateCode),
                    zipCode: toTrimmedString(state?.answers?.mailingZip),
                };
                const result = await post('onlineCart/validateAddress', payload);
                if (!result?.isSuccess) {
                    throw new Error(result?.errorMessage || 'Address validation failed');
                }

                const validationResult = result.data || {};
                const validationErrors = validationResult?.errors || {};

                if (!validationResult?.isValid || validationResult?.meta?.dpvMatchCode?.toUpperCase() !== 'Y') {
                    setMailingInvalid({
                        mailingAddress: validationErrors?.address || 'Address validation failed',
                    });
                    return false;
                }

                // valid and dpv is "Y"
                const suggestedAddressWithMetaData = {
                    ...validationResult?.suggestedAddress,
                    ...validationResult?.meta,
                    shouldDisplayed: Boolean(validationResult?.meta?.hasSuggestionDiff),
                };
                setField('smartySuggestedMalingAddress', suggestedAddressWithMetaData);
                setFieldErrors({
                    mailingAddress: '',
                    mailingAddress1: '',
                    mailingAddress2: '',
                    mailingCity: '',
                    mailingState: '',
                    mailingZip: '',
                });
                setSaveAnswerLoader(false)

                if (validationResult?.meta?.hasSuggestionDiff) return false;
            } catch (error) {
                console.log(error);
                setMailingInvalid({
                    mailingAddress: typeof error.message === 'string' ? error.message : 'Address validation is temporarily unavailable. Please try again.',
                });
                return false;
            }
        }
        
        let isSuccess = false
        let error  = ''
        const nextStep = Math.min(currentStep + 1, totalSteps);
        try {
            setSaveAnswerLoader(true)
            const savedAnswer = await saveAnswers({
                progress: {
                    currentStep: nextStep,
                },
            });
            goToStep(nextStep);
            const latestAnswerId = savedAnswer?.id || '';
            if (currentScreen?.id === 'contact_information') {
                try {
                    const emailResult = await post('onlineCart/sendContinueEmail', {
                        recordId,
                        answerId: latestAnswerId,
                        email: state.answers?.contactEmail || '',
                        businessName: state.answers?.businessName || '',
                    });

                    if (emailResult?.isSuccess) {
                        const sentAt = emailResult?.data?.sentAt || '';
                        const sentTo = emailResult?.data?.email || '';
                        const continueUrl = emailResult?.data?.continueUrl || '';
                        if (sentAt) {
                            setField('continueApplicationEmailSentAt', sentAt);
                        }
                        if (sentTo) {
                            setField('continueApplicationEmailTo', sentTo);
                        }
                        if (continueUrl) {
                            setField('continueApplicationUrl', continueUrl);
                        }
                    }
                } catch (error) {
                    // Continue flow even if email dispatch fails; user can keep progressing.
                }
            }
            if (currentScreen?.id === 'order_summary') {
                let completedAnswers = {
                    ...(state.answers || {}),
                    orderCompleted: true,
                };

                if (!state.answers?.paymentProcessed) {
                    const paymentToken = state.answers?.orderSummary?.paymentToken;
                    const lookupData = state.answers?.orderSummary?.nmiLookupData || null;
                    const lookupExpiry = getCardExpiryFromLookup(lookupData);
                    const fallbackExpiry = String(state.answers?.orderSummary?.expiry || '').trim();
                    const fallbackMonth = fallbackExpiry.includes('/') ? fallbackExpiry.split('/')[0]?.replace(/\D/g, '').slice(0, 2) : '';
                    const fallbackYear = fallbackExpiry.includes('/') ? fallbackExpiry.split('/')[1]?.replace(/\D/g, '').slice(0, 4) : '';
                    const cardMeta = {
                        last4: state.answers?.orderSummary?.cardLast4 || getCardLast4FromLookup(lookupData),
                        expiryMonth: lookupExpiry.expiryMonth || fallbackMonth || '',
                        expiryYear: lookupExpiry.expiryYear || fallbackYear || '',
                    };
                    const chargeResult = await chargeNMITokenOnBackend({
                        token: paymentToken,
                        amount: calculateCheckoutAmount(state.answers, cartProducts),
                        orderContext: {
                            email: state.answers?.contactEmail || '',
                            paymentStep: 'checkout',
                        },
                        cardMeta,
                        recordId,
                        answerId: latestAnswerId,
                    });
                    const paymentResult = chargeResult?.result || {};
                    const customerVaultId = chargeResult?.customerVaultId || '';
                    const paymentCardLast4 = chargeResult?.cardLast4 || cardMeta.last4 || '';
                    const paymentCardExpiryMonth = chargeResult?.expiryMonth || cardMeta.expiryMonth || '';
                    const paymentCardExpiryYear = chargeResult?.expiryYear || cardMeta.expiryYear || '';
                    const totalChargeAmount = Number.isFinite(Number(chargeResult?.chargedAmount)) ? Number(chargeResult.chargedAmount) : calculateCheckoutAmount(state.answers, cartProducts);
                    const orderSummary = state.answers?.orderSummary || {};

                    setField('paymentProcessed', true);
                    setField('paymentCompleted', true);
                    setField('paymentTransactionId', paymentResult?.transactionid || '');
                    setField('paymentOrderId', paymentResult?.orderid || '');
                    setField('paymentAuthCode', paymentResult?.authcode || '');
                    setField('paymentResponseCode', paymentResult?.response_code || '');
                    setField('paymentChargeId', chargeResult?.chargeId || '');
                    setField('paymentCustomerVaultId', customerVaultId);
                    setField('paymentCardLast4', paymentCardLast4);
                    setField('paymentCardExpiryMonth', paymentCardExpiryMonth);
                    setField('paymentCardExpiryYear', paymentCardExpiryYear);
                    setField('totalChargeAmount', totalChargeAmount);
                    setField('orderSummary', {
                        ...orderSummary,
                        customerVaultId,
                        cardLast4: paymentCardLast4 || orderSummary.cardLast4 || '',
                        cardExpiryMonth: paymentCardExpiryMonth,
                        cardExpiryYear: paymentCardExpiryYear,
                    });

                    completedAnswers = {
                        ...completedAnswers,
                        paymentProcessed: true,
                        paymentCompleted: true,
                        paymentTransactionId: paymentResult?.transactionid || '',
                        paymentOrderId: paymentResult?.orderid || '',
                        paymentAuthCode: paymentResult?.authcode || '',
                        paymentResponseCode: paymentResult?.response_code || '',
                        paymentChargeId: chargeResult?.chargeId || '',
                        paymentCustomerVaultId: customerVaultId,
                        paymentCardLast4: paymentCardLast4,
                        paymentCardExpiryMonth: paymentCardExpiryMonth,
                        paymentCardExpiryYear: paymentCardExpiryYear,
                        totalChargeAmount,
                        orderSummary: {
                            ...orderSummary,
                            customerVaultId,
                            cardLast4: paymentCardLast4 || orderSummary.cardLast4 || '',
                            cardExpiryMonth: paymentCardExpiryMonth,
                            cardExpiryYear: paymentCardExpiryYear,
                        },
                    };
                }

                setField('orderCompleted', true);
                await saveAnswers({
                    answers: completedAnswers,
                    progress: {
                        currentStep: totalSteps,
                    },
                });
                setPostCheckoutStage('confirmation');
            }
            isSuccess =  true;
            error = ''
        } catch (err) {
            console.error('Error occurs: ', err);
            isSuccess = false;
            error = err
        } finally {
            setSaveAnswerLoader(false)
        }
        return { isSuccess, error }
    };

    const advanceMailingScreen = async (patch = {}) => {
        setTransitionDirection('forward');
        setFieldErrors({ mailingAddress: '' });
        const nextAnswers = { ...(state.answers || {}), ...patch };
        const nextStep = Math.min(currentStep + 1, totalSteps);
        try {
            setSaveAnswerLoader(true);
            await saveAnswers({
                answers: nextAnswers,
                progress: {
                    currentStep: nextStep,
                },
            });
            // Reflect the choice in local state so back-navigation shows the saved values/flag.
            Object.entries(patch).forEach(([key, val]) => setField(key, val));
            goToStep(nextStep);
        } catch (error) {
            setFieldErrors({
                mailingAddress: typeof error?.message === 'string' ? error.message : 'Unable to continue. Please try again.',
            });
        } finally {
            setSaveAnswerLoader(false);
        }
    };

    const handleProceedWithCurrentMailingAddress = async () => {
        await advanceMailingScreen({
            mailingAddressOverride: {
                overridden: true,
                message: state.errors?.mailingAddress || '',
            },
        });
    };

    const handleUseVerifiedMailingAddress = async () => {
        const suggested = state.answers?.smartySuggestedMalingAddress || {};
        const stateInfo = getStateByCode(suggested.stateCode) || {};
        await advanceMailingScreen({
            mailingAddress1: suggested.addressLine1 || '',
            mailingAddress2: suggested.addressLine2 || '',
            mailingCity: suggested.city || '',
            mailingState: stateInfo.name || state.answers?.mailingState || '',
            mailingStateCode: suggested.stateCode || '',
            mailingZip: suggested.zipCode || '',
            smartySuggestedMalingAddress: { ...suggested, shouldDisplayed: false, selectedAddressType: 'verified' },
            mailingAddressOverride: null,
        });
    };

    const handleKeepMineMailingAddress = async () => {
        const suggested = state.answers?.smartySuggestedMalingAddress || {};
        await advanceMailingScreen({
            smartySuggestedMalingAddress: { ...suggested, shouldDisplayed: false, selectedAddressType: 'original' },
            mailingAddressOverride: {
                overridden: true,
                message: 'We found a verified USPS address that differs from the address you entered.',
            },
        });
    };

    const handleBack = () => {
        setTransitionDirection('backward');
        if (postCheckoutStage === 'confirmation') {
            setPostCheckoutStage('none');
            return;
        }
        dateOfBirthReset(false)
        prevScreen();
    };

    const handleSsnNext = async () => {
        if (typeof ssnSubmitHandlerRef.current !== 'function') {
            return false;
        }

        return ssnSubmitHandlerRef.current();
    };

    const setSaveAnswerLoader = (isLoading)=>{
        ENABLE_NEXT_BUTTON_LOADER && setSaveAnswerLoading(isLoading)
    }

    let renderUI = null;
    if (onlineCartRecordLoading) {
        renderUI = <OnlineCartLoading />;
    } else if (!onlineCartRecord || onlineCartRecordError) {
        // The cart isn't in the portal (not synced from the builder, or an invalid link). Show a
        // message instead of falling back to the static default products, which look like a
        // different cart. A successfully-synced cart always hydrates onlineCartRecord.
        renderUI = <CartUnavailable />;
    } else if (!hasFormData || !currentScreen) {
        renderUI = <NoDataPresent />;
    } else if (currentScreen?.id === 'ssn_collection' && postCheckoutStage === 'none') {
        renderUI = (
            <StepWrapper onBack={handleBack} onNext={handleSsnNext} showBack={currentStep > 1} showNext nextLabel="Next" loading={saveAnswerLoading}>
                <SSNCollection
                    answers={state.answers}
                    onBack={handleBack}
                    showTopBackButton={false}
                    showPaymentSummary={false}
                    showSubmitButton={false}
                    registerSubmitHandler={(handler) => {
                        ssnSubmitHandlerRef.current = handler;
                    }}
                    onComplete={async (payload) => {
                        const nextAnswers = {
                            ...(state.answers || {}),
                            ssnProvided: payload.ssnProvided,
                            ssnFull: payload.ssnFull,
                            ssnLast4: payload.ssnLast4,
                            ssnType: payload.ssnType,
                        };

                        setField('ssnProvided', payload.ssnProvided);
                        setField('ssnFull', payload.ssnFull);
                        setField('ssnLast4', payload.ssnLast4);
                        setField('ssnType', payload.ssnType);
                        
                        setTransitionDirection('forward');
                        setSaveAnswerLoader(true)

                        const nextStep = Math.min(currentStep + 1, totalSteps);
                        await saveAnswers({
                            answers: nextAnswers,
                            progress: {
                                currentStep: nextStep,
                            },
                        });
                        goToStep(nextStep);
                        setSaveAnswerLoader(false)
                    }}
                />
            </StepWrapper>
        );
    } else if (shouldShowConfirmation) {
        renderUI = <OrderConfirmation answers={state.answers} onConsultationBooked={handleConsultationBooked} onSkipConsultation={handleSkipConsultation} />;
    } else {
        renderUI = (
            <StepWrapper
                onBack={handleBack}
                onNext={handleNext}
                loading={saveAnswerLoading}
                showBack={currentStep > 1}
                showNext={currentScreen?.id !== 'order_summary'}
                nextLabel={currentStep === totalSteps - 1 ? 'Continue to checkout' : currentStep === totalSteps ? 'Continue' : 'Next'}
                maxWidth={currentScreen?.id === 'order_summary' ? 980 : undefined}
            >
                <QuestionRenderer
                    screen={currentScreen}
                    answers={state.answers}
                    errors={state.errors}
                    onChange={setField}
                    onNext={handleNext}
                    onProceedWithCurrentAddress={handleProceedWithCurrentMailingAddress}
                    onUseVerifiedAddress={handleUseVerifiedMailingAddress}
                    onKeepMine={handleKeepMineMailingAddress}
                    proceedLoading={saveAnswerLoading}
                />
            </StepWrapper>
        );
    }

    return (
        <Box
            sx={{
                height: '100vh',
                '@supports (height: 100dvh)': {
                    height: '100dvh',
                },
                backgroundColor: ONLINE_CART_TOKENS.background,
                color: ONLINE_CART_TOKENS.textPrimary,
                fontFamily: ONLINE_CART_TOKENS.fontFamily,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <CardHeader
                currentStep={currentStep}
                showProgress={!onlineCartRecordLoading && !shouldShowConfirmation && postCheckoutStage === 'none'}
                onSectionClick={goToStep}
                sections={sectionProgress}
            />
            <main
                ref={mainRef}
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehaviorY: 'contain',
                }}
            >
                <OnlineCartStepTransition
                    transitionKey={shouldShowConfirmation ? 'confirmation' : currentScreen?.id || 'online-cart-empty'}
                    direction={transitionDirection}
                    onStepEntered={resetStepScroll}
                >
                    {renderUI}
                </OnlineCartStepTransition>
            </main>
        </Box>
    );
}

function OnlineCartLoading() {
    return (
        <Box
            sx={{
                minHeight: 'calc(100vh - 96px)',
                display: 'grid',
                placeItems: 'center',
                px: 2,
            }}
        >
            <Box sx={{ display: 'grid', placeItems: 'center', gap: 1.5 }}>
                <CircularProgress size={28} />
                <Typography sx={{ fontSize: '0.95rem', color: ONLINE_CART_TOKENS.textMutedSoft }}>Loading your cart...</Typography>
            </Box>
        </Box>
    );
}

function NoDataPresent() {
    return (
        <>
            <Box
                sx={{
                    minHeight: 'calc(100vh - 96px)',
                    display: 'grid',
                    placeItems: 'center',
                    px: 2,
                }}
            >
                <NoDataPresentUI
                    heading="No data present"
                    containerSx={{
                        maxWidth: 520,
                        width: '100%',
                        border: `1px solid ${ONLINE_CART_TOKENS.border}`,
                        borderRadius: 2,
                        backgroundColor: ONLINE_CART_TOKENS.surface,
                        p: { xs: 2.5, sm: 3 },
                        textAlign: 'center',
                        height: 'auto',
                        gap: 1,
                    }}
                    footerButton={
                        <Typography sx={{ mt: 1, fontSize: '0.95rem', lineHeight: 1.5, color: ONLINE_CART_TOKENS.textMutedSoft }}>
                            We could not load the Online Cart form data. Please refresh and try again.
                        </Typography>
                    }
                />
            </Box>
        </>
    );
}

// Shown when the requested cart isn't found in the portal (e.g. previewed from the builder before
// being synced). Replaces the old behavior of silently falling back to the static default products.
function CartUnavailable() {
    return (
        <Box
            sx={{
                minHeight: 'calc(100vh - 96px)',
                display: 'grid',
                placeItems: 'center',
                px: 2,
            }}
        >
            <NoDataPresentUI
                heading="Cart not available"
                containerSx={{
                    maxWidth: 520,
                    width: '100%',
                    border: `1px solid ${ONLINE_CART_TOKENS.border}`,
                    borderRadius: 2,
                    backgroundColor: ONLINE_CART_TOKENS.surface,
                    p: { xs: 2.5, sm: 3 },
                    textAlign: 'center',
                    height: 'auto',
                    gap: 1,
                }}
                footerButton={
                    <Typography sx={{ mt: 1, fontSize: '0.95rem', lineHeight: 1.5, color: ONLINE_CART_TOKENS.textMutedSoft }}>
                        This cart hasn’t been published to the portal yet. If you’re previewing from the cart builder, sync the cart first, then reopen the preview.
                    </Typography>
                }
            />
        </Box>
    );
}

export default function OnlineCart() {
    const { recordId: routeRecordId } = useParams();
    const [searchParams] = useSearchParams();
    const recordId = routeRecordId || DEFAULT_ONLINE_CART_RECORD_ID;
    const initialAnswerId = getPersistedOnlineCartAnswerId(recordId, searchParams);

    return (
        <OnlineCartProvider key={recordId} recordId={recordId} initialAnswerId={initialAnswerId}>
            <OnlineCartScreen />
        </OnlineCartProvider>
    );
}
